import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const KIND_OPTIONS = [
    { value: "supervisor_notes", label: "Supervisor notes" },
    { value: "scene_notes", label: "Scene notes" },
    { value: "character_notes", label: "Character notes" },
    { value: "reference", label: "Reference" },
    { value: "other", label: "Other" },
];

/**
 * Iter-97 PART 6 — owner+admin can correct an uploaded document's
 * metadata without re-uploading the file.
 */
export default function EditChangeDocumentDialog({
    open,
    onOpenChange,
    document: doc,
    scenes = [],
    characters = [],
    onSaved,
}) {
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("supervisor_notes");
    const [sceneId, setSceneId] = useState("");
    const [characterId, setCharacterId] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !doc) return;
        setLabel(doc.label || "");
        setKind(doc.kind || "supervisor_notes");
        setSceneId(doc.scene_id || "");
        setCharacterId(doc.character_id || "");
        setDescription(doc.description || "");
        setError("");
    }, [open, doc]);

    const submit = async () => {
        setBusy(true);
        setError("");
        try {
            const payload = {
                label: label,
                kind: kind,
                scene_id: sceneId,
                character_id: characterId,
                description: description,
            };
            await onSaved(payload);
            onOpenChange(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Save failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                data-testid="edit-change-document-dialog"
                className="max-w-md bg-card border-violet-500/30"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-lg font-bold text-white">
                        Edit document
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Update label, type, scene/character link or description. The file itself stays unchanged.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-2">
                    <div>
                        <Label htmlFor="ecd-label" className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Label / title
                        </Label>
                        <Input
                            id="ecd-label"
                            data-testid="edit-change-document-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Day 12 supervisor notes"
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>

                    <div>
                        <Label htmlFor="ecd-kind" className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Type / category
                        </Label>
                        <select
                            id="ecd-kind"
                            data-testid="edit-change-document-kind"
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                        >
                            {KIND_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ecd-scene" className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Scene
                            </Label>
                            <select
                                id="ecd-scene"
                                data-testid="edit-change-document-scene"
                                value={sceneId}
                                onChange={(e) => setSceneId(e.target.value)}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                            >
                                <option value="">— None —</option>
                                {scenes.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        Sc {s.scene_number || "?"}
                                        {s.heading ? ` · ${s.heading}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="ecd-char" className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Character
                            </Label>
                            <select
                                id="ecd-char"
                                data-testid="edit-change-document-character"
                                value={characterId}
                                onChange={(e) => setCharacterId(e.target.value)}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                            >
                                <option value="">— None —</option>
                                {characters.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ecd-desc" className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Description / notes
                        </Label>
                        <Textarea
                            id="ecd-desc"
                            data-testid="edit-change-document-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            maxLength={4000}
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y"
                        />
                    </div>

                    {error ? (
                        <p data-testid="edit-change-document-error" className="text-xs text-red-400">{error}</p>
                    ) : null}

                    <div className="flex gap-2 pt-2">
                        <Button
                            type="button"
                            data-testid="edit-change-document-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="edit-change-document-save"
                            onClick={submit}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
